import { useEffect, useMemo } from 'react';
import moment from 'moment';
import { FieldValues, Path, useFormContext } from 'react-hook-form';
import { isiOS } from './isPlatform';

/**
 * Hook that handles a from and to date range. Making sure they are valid and that minDate and default values are sensible
 * @returns start and end settings
 */
const useTimeRangeSettings = <
    FormValues extends FieldValues,
    StartName extends Path<FormValues>,
    EndName extends Path<FormValues>,
>({
    startName,
    endName,
    defaultStart = moment().add({ hour: 1 }).startOf('hour').toDate(),
}: {
    startName: StartName;
    endName: EndName;
    defaultStart?: Date;
}): {
    [K in StartName | EndName]: { defaultValue: Date; minDate: Date; name: K };
} => {
    const { watch, setValue } = useFormContext<FormValues>();
    const start = watch(startName);
    const end = watch(endName);

    const defaultEnd = moment(start ? start : defaultStart)
        .add({ minutes: 30 })
        .endOf('minute')
        .toDate();

    const initialMinStart = useMemo(() => new Date(), []);
    const initialMinEnd = useMemo(() => moment(start), [start]);

    useEffect(() => {
        if (start && end) {
            if (moment(end).isBefore(start)) {
                if (isiOS()) {
                    setValue(endName, initialMinEnd.toDate() as typeof end);
                } else {
                    // Because timepicker on android does not support minimumDate we change it up a bit
                    setValue(endName, start);
                }
            }
        }
    }, [end, start, setValue, endName, initialMinEnd]);

    return {
        [startName]: {
            defaultValue: defaultStart,
            minDate: start ? moment.min(moment(start), moment(initialMinStart)).toDate() : initialMinStart,
            name: startName,
        },
        [endName]: {
            defaultValue: defaultEnd,
            minDate: end ? moment.min(moment(end), initialMinEnd).toDate() : initialMinEnd.toDate(),
            name: endName,
        },
    } as {
        [K in StartName | EndName]: { defaultValue: Date; minDate: Date; name: K };
    };
};

export default useTimeRangeSettings;
